import { useEffect, useState } from "react";
import "./styles.scss"
import { fabric } from 'fabric';
import { Square, Circle, Triangle, RectangleHorizontal, Octagon, MessageSquare, MoveRight, Minus, RotateCcw, RotateCw, Bold, MapPin, Type, Italic, FlipHorizontal, FlipVertical, BringToFront, Pen } from 'lucide-react';
import { AlphaPicker } from 'react-color';
import { useCanvas } from "../context/canvasContext";
import axios from "axios";
import { toast } from "react-toastify";
import { toAbsoluteUrl } from "../../../../_metronic/helpers";
import { truckSide, truckTop, trailerSide, trailerTop } from "./vehicleIcon";
import { bathroom_1, bathroom_2, bathroom_3, bathroom_4, shower, garbage } from "./customIcon";
import { colors, strokes, hexToRgba, addIdToObject } from "../utils/index";

const fontFamilies = [
  'Arial',
  'Inter',
  "IBM Plex Sans",
  "Roboto Slab",
];

const fontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72];
const strokeSizes = [1, 2, 4, 6, 8, 10, 15, 20, 30]
const shapes = ['Square', 'Circle', 'Triangle', 'Rectangle', 'Stop', 'StopSign', 'Quote', 'Arrow', 'ArrowCurve', 'Line', 'Pin', 'Type', 'Draw', 'PolygonShape', 'Logo', 'LandingGear','Enter', 'Exit', 'Garbage', 'TruckSide', 'TruckTop', 'TrailerSide', 'TrailerTop', 'Bathroom1', 'Bathroom2', 'Bathroom3', 'Bathroom4', 'Shower', 'GateAccess'];

const Sidebar = ({
  getListing,
  getAllImages,
  setOpenPhotoEditModal,
}) => {
  const { canvasRef, canvasLoading, imagePreview, colorType, setColorType, fillColor, setFillColor, mainColor, setMainColor, strokeColor, setStrokeColor, strokeWidth, setStrokeWidth, fontFamily, setFontFamily, fontSize, setFontSize, isBold, setIsBold, isItalic, setIsItalic, selectedShape, selectedImage, setImagePreview, setCanvasLoading, setSelectedShape, isDrawing, setIsDrawing, polygonPoints, setPolygonPoints, lines, setLines, shapeType, setShapeType } = useCanvas();
  const [loading, setLoading] = useState(false);
  const onShapeSelection = (shape) => {
    switch (shape) {
      case 'Square':
        return onAddSquare();
      case 'Circle':
        return onAddCircle();
      case 'Triangle':
        return onAddTriangle();
      case 'Rectangle':
        return onAddRectangle();
      case 'Stop':
        return onAddStop();
      case 'Quote':
        return onAddQuote();
      case 'Arrow':
        return onAddArrow();
      case 'ArrowCurve':
        return onAddArrowCurve();
      case 'Line':
        return onAddLine();
      case 'Pin':
        return onAddPin();
      case 'Logo':
        return onAddImage("/media/logos/logo.png");
      case 'LandingGear':
        return onLandingGearIcon();
      case 'Enter':
        return onAddImage("/media/logos/enter.png");
      case 'Exit':
        return onAddImage("/media/logos/exit.png");
      case 'StopSign':
        return onAddImage("/media/logos/stopsign.png");
      case 'Garbage':
        return onAddCustomIcon('garbage');
      case 'TruckTop':
        return onAddTruck('trucktop');
      case 'TruckSide':
        return onAddTruck('truckside');
      case 'TrailerTop':
        return onAddTruck('trailertop');
      case 'TrailerSide':
        return onAddTruck('trailerside');
      case 'Bathroom1':
        return onAddCustomIcon('bathroom_1');
      case 'Bathroom2':
        return onAddCustomIcon('bathroom_2');
      case 'Bathroom3':
        return onAddCustomIcon('bathroom_3');
      case 'Bathroom4':
        return onAddCustomIcon('bathroom_4');
      case 'Shower':
        return onAddCustomIcon('shower');
      case 'Type':
        return onAddType();
      case 'Draw':
        return onDraw();
      case 'PolygonShape':
        return onAddPolygon();
      case 'GateAccess':
        return onAddImage("/media/logos/gateaccess.png");
      default:
        break;
    }
  }

  const distance = (p1, p2) => {
    return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
  };

  const addPoint = (event) => {
    if (canvasRef?.current && isDrawing && shapeType === 'polygonshape') {
      canvasRef.current.defaultCursor = 'crosshair';
      const pointer = canvasRef?.current?.getPointer(event.e);
      let newPoint = { x: pointer?.x ?? 0, y: pointer?.y ?? 0 };

      const firstPoint = polygonPoints[0];
      if (polygonPoints.length > 2 && distance(newPoint, firstPoint) < 10) {
        stopPolygon();
        return;
      }

      const points = [...polygonPoints, newPoint];
      setPolygonPoints(points);

      const newLine = new fabric.Line([newPoint.x, newPoint.y, newPoint.x, newPoint.y], {
        stroke: '#32cc71',
        strokeWidth: 4,
        fill: hexToRgba('#32cc71', 1),
        lockRotation: false,
        selectable: true,
        hasControls: true,
      });

      canvasRef?.current?.add(newLine);
      setLines([...lines, newLine]);
      canvasRef?.current?.renderAll();
    }
  };

  const moveLine = (event) => {
    if (canvasRef?.current && isDrawing && polygonPoints.length > 0 && shapeType === 'polygonshape') {
      const pointer = canvasRef.current.getPointer(event.e);

      lines[lines.length - 1]?.set({ x2: pointer.x, y2: pointer.y });

      const firstPoint = polygonPoints[0];
      const isNearStart = distance(pointer, firstPoint) < 10;

      canvasRef.current.defaultCursor = isNearStart ? 'pointer' : 'crosshair';

      const existingIndicator = canvasRef.current.getObjects().find(obj => obj.id === 'hover-indicator');

      if (isNearStart && !existingIndicator) {
        const indicator = new fabric.Circle({
          left: firstPoint.x,
          top: firstPoint.y,
          radius: 6,
          fill: 'rgba(255,0,0,0.5)',
          selectable: false,
          evented: false,
          id: 'hover-indicator',
          originX: 'center',
          originY: 'center'
        });
        canvasRef.current.add(indicator);
      } else if (!isNearStart && existingIndicator) {
        canvasRef.current.remove(existingIndicator);
      }

      canvasRef.current.renderAll();
    }
  };

  const stopPolygon = (event = null) => {
    if (canvasRef?.current && isDrawing && shapeType === 'polygonshape') {
      if (polygonPoints.length > 2) {
        const polygon = new fabric.Polygon(polygonPoints, {
          fill: hexToRgba('#32cc71', 0.20),
          stroke: '#32cc71',
          strokeWidth: 4,
          selectable: true,
          hasControls: true,
          lockRotation: false,
        });

        canvasRef.current.add(polygon);

        const indicator = canvasRef.current.getObjects().find(obj => obj.id === 'hover-indicator');
        if (indicator) {
          canvasRef.current.remove(indicator);
        }

        setPolygonPoints([]);

        for (let line of lines) {
          canvasRef.current.remove(line);
        }

        setLines([]);
        canvasRef.current.defaultCursor = 'default';
        setIsDrawing(false);
        setShapeType(null);
        canvasRef.current.selection = true;
        canvasRef.current.getObjects().forEach((obj) => {
          obj.selectable = true;
          obj.evented = true;
        })
        canvasRef.current.renderAll();
      }
    }
  };

  useEffect(() => {
    canvasRef?.current?.on('mouse:down', addPoint);
    canvasRef?.current?.on('mouse:move', moveLine);
    canvasRef?.current?.on('mouse:dblclick', stopPolygon);

    return () => {
      canvasRef?.current?.off('mouse:down', addPoint);
      canvasRef?.current?.off('mouse:move', moveLine);
      canvasRef?.current?.off('mouse:dblclick', stopPolygon);
    };
  }, [polygonPoints, isDrawing, shapeType, lines]);


  const onAddPolygon = () => {
    setIsDrawing(true)
    setShapeType('polygonshape')
    canvasRef.current.selection = false;
    canvasRef.current.getObjects().forEach((obj) => {
      obj.selectable = false;
      obj.evented = false;
    })
    canvasRef.current.defaultCursor = 'crosshair';
    canvasRef?.current?.renderAll();
  }

  const deleteObject = (_eventData, transform) => {
    const canvas = transform.target.canvas;
    canvas.remove(transform.target);
    canvas.requestRenderAll();
  }

  const cloneObject = (_eventData, transform) => {
    const canvas = transform.target.canvas;
    transform.target.clone((cloned) => {
      cloned.left += 10;
      cloned.top += 10;
      cloned.controls.deleteControl = transform.target.controls.deleteControl;
      cloned.controls.cloneControl = transform.target.controls.cloneControl;
      canvas.add(cloned);
      canvas.bringToFront(cloned);
      canvas.requestRenderAll();
    });
  }

  const deleteIcon =
    "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg version='1.1' id='Ebene_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='595.275px' height='595.275px' viewBox='200 215 230 470' xml:space='preserve'%3E%3Ccircle style='fill:%23F44336;' cx='299.76' cy='439.067' r='218.516'/%3E%3Cg%3E%3Crect x='267.162' y='307.978' transform='matrix(0.7071 -0.7071 0.7071 0.7071 -222.6202 340.6915)' style='fill:white;' width='65.545' height='262.18'/%3E%3Crect x='266.988' y='308.153' transform='matrix(0.7071 0.7071 -0.7071 0.7071 398.3889 -83.3116)' style='fill:white;' width='65.544' height='262.179'/%3E%3C/g%3E%3C/svg%3E";


  const cloneIcon =
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48cGF0aCAgZmlsbD0iI2ZmZiIgZD0iTTQ2NCAwYzI2LjUgMCA0OCAyMS41IDQ4IDQ4djI4OGMwIDI2LjUtMjEuNSA0OC00OCA0OEgxNzZjLTI2LjUgMC00OC0yMS41LTQ4LTQ4VjQ4YzAtMjYuNSAyMS41LTQ4IDQ4LTQ4aDI4OE0xNzYgNDE2Yy00NC4xIDAtODAtMzUuOS04MC04MFYxMjhINDhjLTI2LjUgMC00OCAyMS41LTQ4IDQ4djI4OGMwIDI2LjUgMjEuNSA0OCA0OCA0OGgyODhjMjYuNSAwIDQ4LTIxLjUgNDgtNDh2LTQ4SDE3NnoiLz48L3N2Zz4="

  const deleteImg = document.createElement('img');
  deleteImg.src = deleteIcon;

  const cloneImg = document.createElement('img');
  cloneImg.src = cloneIcon;

  const renderIcon = (icon) => {
    return function (ctx, left, top, _styleOverride, fabricObject) {
      const size = this.cornerSize;
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
      ctx.drawImage(icon, -size / 2, -size / 2, size, size);
      ctx.restore();
    };
  }

  const onAddSquare = () => {
    const square = new fabric.Rect({
      width: 100,
      height: 100,
      fill: hexToRgba('#32cc71', 0.20),
      stroke: '#32cc71',
      strokeWidth: 4,
      strokeUniform: true,
      left: 100,
      top: 100
    })

    canvasRef.current.add(square)
    canvasRef.current.renderAll()
  }
  const onAddCircle = () => {
    const circle = new fabric.Circle({
      radius: 50,
      fill: hexToRgba('#32cc71', 0.20),
      stroke: '#32cc71',
      strokeWidth: 4,
      strokeUniform: true,
      left: 100,
      top: 100
    });
    canvasRef.current.add(circle);
    canvasRef.current.renderAll()
  }
  const onAddTriangle = () => {
    const triangle = new fabric.Triangle({
      width: 100,
      height: 100,
      fill: hexToRgba('#32cc71', 0.20),
      stroke: strokeColor,
      strokeWidth: 4,
      strokeUniform: true,
      left: 100,
      top: 100
    })

    canvasRef.current.add(triangle);
    canvasRef.current.renderAll()
  }
  const onAddRectangle = () => {
    const rect = new fabric.Rect({
      width: 150,
      height: 60,
      fill: hexToRgba('#32cc71', 0.20),
      stroke: '#32cc71',
      strokeWidth: 4,
      strokeUniform: true,
      left: 100,
      top: 100,
    });
    canvasRef.current.add(rect);
    canvasRef.current.renderAll()

  }
  const onAddStop = () => {
    const size = 80;
    const points = [];
    const sides = 8;

    for (let i = 0; i < sides; i++) {
      const angle = (i * Math.PI * 2) / sides - Math.PI / sides;
      points.push({
        x: size * Math.cos(angle) + size,
        y: size * Math.sin(angle) + size
      });
    }

    const stopSign = new fabric.Polygon(points, {
      left: 100,
      top: 100,
      fill: hexToRgba('#ff403d', 1),
      stroke: '#000000',
      strokeWidth: 4,
      strokeUniform: true,
    });

    canvasRef.current.add(stopSign);
    canvasRef.current.renderAll();
  }
  const onAddQuote = () => {
    const path = new fabric.Path('M 20,20' +
      'Q 0,20 0,40' +
      'L 0,160' +
      'Q 0,180 20,180' +
      'L 140,180' +
      'Q 160,180 160,160' +
      'L 160,100' +
      'L 190,130' +
      'L 160,80' +
      'L 160,40' +
      'Q 160,20 140,20' +
      'Z', {
      left: 100,
      top: 100,
      fill: hexToRgba('#ffb800', 1),
      stroke: '#000000',
      strokeWidth: 6,
      strokeUniform: true,
    });

    canvasRef.current.add(path);
    canvasRef.current.renderAll();
  }
  const onAddArrow = () => {
    const points = [
      { x: 0, y: 20 },
      { x: 80, y: 20 },
      { x: 80, y: 0 },
      { x: 100, y: 25 },
      { x: 80, y: 50 },
      { x: 80, y: 30 },
      { x: 0, y: 30 }
    ];

    const arrow = new fabric.Polygon(points, {
      left: 100,
      top: 100,
      fill: hexToRgba('#ff403d', 1),
      stroke: '#000000',
      strokeWidth: 4,
      strokeUniform: true
    });

    canvasRef.current.add(arrow);
    canvasRef.current.renderAll();
  }

  const onAddArrowCurve = () => {
    const uniqueId = Math.random().toString(36).substring(7);
    const start = { x: 50, y: 200 };
    const control = { x: 125, y: 200 };
    const end = { x: 175, y: 200 };

    const canvas = canvasRef.current;
    let isGrouped = true;
    let group, curve, startCircle, controlCircle, endCircle, arrowHead;

    curve = new fabric.Path(`M ${start.x} ${start.y} Q ${control.x} ${control.y} ${end.x} ${end.y}`, {
      stroke: "#ff403d",
      strokeWidth: 5,
      fill: '',
      selectable: true,
      hasControls: false,
      objectCaching: false,
      id: `${uniqueId}`,
      shape: 'arrow',
      shapeType: 'curve',
      lockMovementX: true,
      lockMovementY: true,
      hasBorders: false
    });

    startCircle = new fabric.Circle({
      left: start.x,
      top: start.y,
      radius: 5,
      fill: '#32cc71',
      originX: 'center',
      originY: 'center',
      hasControls: false,
      id: `${uniqueId}`,
      shape: 'arrow',
      shapeType: 'start-circle',
      visible: false,
      hasBorders: false
    });

    controlCircle = new fabric.Circle({
      left: control.x,
      top: control.y,
      radius: 5,
      fill: '#32cc71',
      originX: 'center',
      originY: 'center',
      hasControls: false,
      id: `${uniqueId}`,
      shape: 'arrow',
      shapeType: 'control-circle',
      visible: false,
      hasBorders: false
    });

    endCircle = new fabric.Circle({
      left: end.x,
      top: end.y,
      radius: 5,
      fill: '#32cc71',
      originX: 'center',
      originY: 'center',
      hasControls: false,
      id: `${uniqueId}`,
      shape: 'arrow',
      shapeType: 'end-circle',
      selectable: false,
      hasBorders: false
    });

    arrowHead = new fabric.Triangle({
      width: 20,
      height: 20,
      fill: '#ff403d',
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      left: end.x,
      top: end.y,
      angle: 90,
      id: `${uniqueId}`,
      shape: 'arrow',
      shapeType: 'arrow-head',
      hasBorders: false
    });

    [curve, startCircle, controlCircle, endCircle, arrowHead].forEach(obj => addIdToObject(obj));
    function updateCurve() {
      const p0 = startCircle.getCenterPoint();
      const p1 = controlCircle.getCenterPoint();
      const p2 = endCircle.getCenterPoint();
      curve.set({
        path: [
          ['M', p0.x, p0.y],
          ['Q', p1.x, p1.y, p2.x, p2.y],
        ],
      });

      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;

      arrowHead.set({
        left: p2.x,
        top: p2.y,
        angle: angle
      });

      canvas.requestRenderAll();
    }

    function addEventListeners() {
      [startCircle, controlCircle].forEach(c => {
        c.on('moving', updateCurve);
        c.on('mousedblclick', toggleGroup);
      });
      curve.on('mousedblclick', toggleGroup);
      endCircle.on('mousedblclick', toggleGroup);
    }

    function toggleGroup() {
      if (isGrouped) {
        const objects = group._restoreObjectsState().getObjects();
        canvas.remove(group);
        const p0 = objects[1].getCenterPoint();
        const p1 = objects[2].getCenterPoint();
        const p2 = objects[3].getCenterPoint();
        curve = new fabric.Path(`M ${p0.x} ${p0.y} Q ${p1.x} ${p1.y} ${p2.x} ${p2.y}`, {
          stroke: objects[0].stroke,
          strokeWidth: 5,
          fill: '',
          selectable: true,
          hasControls: false,
          hasBorders: false,
          objectCaching: false,
          id: objects[0].id,
          shape: objects[0].shape,
          shapeType: objects[0].shapeType,
          lockMovementX: true,
          lockMovementY: true
        });
        addIdToObject(curve);
        startCircle = objects[1]
        startCircle.set({ visible: true });
        controlCircle = objects[2]
        controlCircle.set({ visible: true });
        endCircle = objects[3]
        arrowHead = objects[4]
        addEventListeners();

        canvasRef.current.add(curve, startCircle, controlCircle, endCircle, arrowHead)
        updateCurve();
        isGrouped = false;
      } else {
        [curve, startCircle, controlCircle, endCircle, arrowHead].forEach(obj => canvas.remove(obj));
        startCircle.set({ visible: false });
        controlCircle.set({ visible: false });
        group = new fabric.Group([curve, startCircle, controlCircle, endCircle, arrowHead], {
          selectable: true,
          hasControls: true,
          lockScalingX: true,
          lockScalingY: true,
          id: `${uniqueId}`,
          shape: 'arrow'
        });
        group.on('mousedblclick', toggleGroup);
        canvas.add(group);
        isGrouped = true;
      }

      canvas.renderAll();
    }

    group = new fabric.Group([curve, startCircle, controlCircle, endCircle, arrowHead], {
      selectable: true,
      hasControls: true,
      lockScalingX: true,
      lockScalingY: true,
      id: `${uniqueId}`,
      shape: 'arrow'
    });
    fabric.Group.prototype.toObject = (function (toObject) {
      return function (propertiesToInclude) {
        return fabric.util.object.extend(toObject.call(this, propertiesToInclude), {
          id: this.id,
          shape: this.shape,
        });
      };
    })(fabric.Group.prototype.toObject);
    group.on('mousedblclick', toggleGroup);
    canvas.add(group);
  };

  const onAddLine = () => {
    const line = new fabric.Line([100, 100, 200, 200], {
      stroke: fillColor,
      strokeWidth: strokeWidth,
      left: 100,
      top: 100
    });
    canvasRef.current.add(line);
    canvasRef.current.renderAll()
  }

  const onAddPin = () => {
    fabric.loadSVGFromString(
      `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>`,
      (objects, options) => {
        const obj = fabric.util.groupSVGElements(objects, options);
        obj.getObjects().forEach((path) => {
          path.set({ fill: hexToRgba('#ff403d', 1), stroke: '#000000' });
        });
        obj.set({ left: 100, top: 100 });
        canvasRef.current.add(obj);
        canvasRef.current.renderAll();
      }
    );
  }

  const onAddImage = (image) => {
    const convertImageToBase64 = async () => {
      try {
        const response = await fetch(toAbsoluteUrl(image));
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          fabric.Image.fromURL(reader.result, (image) => {
            image.scaleToHeight(60);
            image.set({
              left: 0,
              top: 0,
              crossOrigin: "Anonymous"
            });
            canvasRef.current.add(image);
            canvasRef.current.renderAll();
          });
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error loading image: ", error);
      }
    };
    convertImageToBase64();
  };

  const onAddTruck = (type) => {
    const iconType = {
      'trailertop': trailerTop,
      'trailerside': trailerSide,
      'trucktop': truckTop,
      'truckside': truckSide,
    }
    fabric.loadSVGFromString(iconType[type], (objects, options) => {
      const obj = fabric.util.groupSVGElements(objects, options);
      obj.getObjects().forEach((path) => {
        path.set({ stroke: '#000000', strokeWidth: 5, fill: hexToRgba('#ffb800', 1) });
      });
      obj.set({ left: 100, top: 100 });
      canvasRef.current.add(obj);
      canvasRef.current.renderAll()
    })
  }

  const onAddCustomIcon = (type) => {
    const iconName = {
      'bathroom_1': bathroom_1,
      'bathroom_2': bathroom_2,
      'bathroom_3': bathroom_3,
      'bathroom_4': bathroom_4,
      'shower': shower,
      'garbage': garbage
    }
    fabric.loadSVGFromString(iconName[type],
      (objects, options) => {
        const obj = fabric.util.groupSVGElements(objects, options);
        if (type === 'garbage') {
          obj.set({ left: 100, top: 100, fill: hexToRgba('#808080', 1), id: 'garbageIcon' });
          obj.toObject = (function (toObject) {
            return function () {
              return fabric.util.object.extend(toObject.call(this), {
                id: this.id,
              });
            };
          })(obj.toObject);
        } else {
          obj.getObjects().forEach((path) => {
            if (path.path) {
              path.set({ fill: hexToRgba('#004882', 1) });
            }
          });
          obj.set({ left: 100, top: 100, id: 'customIconNew' });
          fabric.Group.prototype.toObject = (function (toObject) {
            return function (propertiesToInclude) {
              return fabric.util.object.extend(toObject.call(this, propertiesToInclude), {
                id: this.id
              });
            };
          })(fabric.Group.prototype.toObject);
        }
        canvasRef.current.add(obj);
        canvasRef.current.renderAll();
      }
    );
  }

  const onAddType = () => {
    const text = new fabric.Textbox('Type Here', {
      width: 120,
      left: 100,
      top: 100,
      fill: '#020000',
      fontSize: 42,
      fontWeight: isBold ? 'bold' : 'normal',
      fontStyle: isItalic ? 'italic' : 'normal',
      fontFamily: 'Roboto Slab',
      editable: true,
      selectable: true,
      BringToFront: true
    })
    canvasRef.current.add(text);
    canvasRef.current.renderAll()
  }

  const onDraw = () => {
    if (isDrawing && shapeType === 'draw') {
      canvasRef.current.isDrawingMode = false;
      setIsDrawing(false);
      canvasRef.current.defaultCursor = 'default';
      setPolygonPoints([]);
      setLines([]);
      setShapeType(null);
    } else {
      fabric.Path.prototype.toObject = (function (toObject) {
        return function (propertiesToInclude) {
          return toObject.call(this, ['id'].concat(propertiesToInclude || []));
        };
      })(fabric.Path.prototype.toObject);
      canvasRef.current.isDrawingMode = true;
      setIsDrawing(true);
      setShapeType('draw');
      canvasRef.current.freeDrawingBrush.color = strokeColor;
      canvasRef.current.freeDrawingBrush.width = strokeWidth;
      canvasRef.current.renderAll();
    }
  }

  const onLandingGearIcon = () => {
    const textContent = 'LANDING GEAR SUPPORT REQUIRED';
    const rect = new fabric.Rect({
      width: 310,
      height: 80,
      originX: 'left',
      originY: 'top',
      fill: hexToRgba('#ff403d', 1),
      stroke: '#ffffff',
      strokeWidth: 2
    });
    const text = new fabric.Textbox(textContent, {
      width: 302,
      fontSize: 16,
      fontFamily: 'Roboto Slab',
      fontWeight: 'bolder',
      fill: '#ffffff',
      textAlign: 'center',
      originX: 'left',
      originY: 'top',
      left: 5,
      top: 80 / 2 - 16 / 2, 
    });


    const group = new fabric.Group([rect, text], {
      left: canvasRef.current.width - 312,
      top: canvasRef.current.height - 82,
      id: 'customText',
    });

    fabric.Group.prototype.toObject = (function (toObject) {
      return function (propertiesToInclude) {
        return fabric.util.object.extend(toObject.call(this, propertiesToInclude), {
          id: this.id,
        });
      };
    })(fabric.Group.prototype.toObject);
    canvasRef.current.add(group);
    canvasRef.current.renderAll()
  }

  const onRotate = (type) => {
    if (canvasRef && canvasRef.current) {
      const canvas = canvasRef.current;
      const backgroundImage = canvas.backgroundImage;

      if (backgroundImage) {
        const fixedWidth = canvas.getWidth();
        const fixedHeight = canvas.getHeight();

        let currentAngle = backgroundImage.angle || 0;
        let newAngle;

        if (type === 'right') {
          newAngle = currentAngle + 90;
          newAngle = newAngle % 360;
        } else {
          newAngle = currentAngle - 90;
          newAngle = ((newAngle % 360) + 360) % 360;
        }

        backgroundImage.set({
          angle: newAngle,
          originX: 'center',
          originY: 'center',
          left: fixedWidth / 2,
          top: fixedHeight / 2
        });

        const angleInRadians = fabric.util.degreesToRadians(newAngle);
        const origWidth = backgroundImage.width;
        const origHeight = backgroundImage.height;
        const rotatedWidth = Math.abs(origWidth * Math.cos(angleInRadians)) +
          Math.abs(origHeight * Math.sin(angleInRadians));
        const rotatedHeight = Math.abs(origWidth * Math.sin(angleInRadians)) +
          Math.abs(origHeight * Math.cos(angleInRadians));
        const scaleX = fixedWidth / rotatedWidth;
        const scaleY = fixedHeight / rotatedHeight;
        const scale = Math.max(scaleX, scaleY);

        backgroundImage.set({
          scaleX: [0, 180, 360].includes(newAngle) ? scaleX : scale,
          scaleY: [0, 180, 360].includes(newAngle) ? scaleY : scale,
        });
        canvas.renderAll();
      }
    }
  };

  const onFlip = (type) => {
    if (canvasRef) {
      if (type === 'horizontal') {
        canvasRef.current.backgroundImage.flipX = !canvasRef.current.backgroundImage.flipX
      } else {
        canvasRef.current.backgroundImage.flipY = !canvasRef.current.backgroundImage.flipY
      }
      canvasRef.current.renderAll()
    }
  }

  const onBringToFront = () => {
    const activeObj = canvasRef.current.getActiveObject();
    if (activeObj) {
      canvasRef.current.bringToFront(activeObj).discardActiveObject(activeObj).renderAll();
    }
  }

  const onSaveImage = async () => {
    if (canvasRef) {
      setLoading(true)
      const shapes = canvasRef.current.getObjects().filter(obj => obj.shape);
      const groupedById = {};
      shapes.forEach(item => {
        const id = item.id;
        if (!groupedById[id]) {
          groupedById[id] = [];
        }
        groupedById[id].push(item);
      });

      Object.values(groupedById).forEach(shapeGroup => {
        if (shapeGroup.length === 1 && shapeGroup[0].type === 'group') return;
        shapeGroup.forEach(item => {
          if (item.shapeType === 'start-circle' || item.shapeType === 'control-circle') {
            item.set({ visible: false });
          } 
          canvasRef.current.remove(item)
        })

        const group = new fabric.Group(shapeGroup, {
          selectable: true,
          hasControls: true,
          lockScalingX: true,
          lockScalingY: true,
          id: shapeGroup[0].id,
          shape: shapeGroup[0].shape
        });
        canvasRef.current.add(group);
        canvasRef.current.renderAll();
      });

      const dataURL = canvasRef.current.toDataURL({
        format: 'png',
        quality: 1
      });
      const blobFile = await fetch(dataURL).then(file => file.blob());
      const json = canvasRef.current.toJSON();
      const canvas = {
        width: canvasRef.current.getWidth(),
        height: canvasRef.current.getHeight()
      }
      const jsonString = JSON.stringify({...json, canvas});
      const blobJSON = new Blob([jsonString], { type: 'application/json' });
      const formData = new FormData();
      formData.append('asset_id', selectedImage.asset_id);
      formData.append('image', blobFile, `${new Date().getMilliseconds()}.png`);
      formData.append('layers', blobJSON, 'layers.json');
      axios.put(
        `${process.env.REACT_APP_API_V3_URL}/listing/images`,
        formData
      )
        .then(res => {
          toast.success('Image saved successfully')
          getListing()
          getAllImages()
          setImagePreview(null)
          setSelectedShape(null)
          setCanvasLoading(false)
          setOpenPhotoEditModal(false)
          canvasRef.current = null
          setLoading(false)
        })
        .catch(err => {
          if (err && err.response && err.response?.data && err.response?.data?.message) {
            toast.error(err.response.data.message);
          } else {
            toast.error("Technical Error, Please Try Again");
          }
          setLoading(false)
        })
    }
  }

  useEffect(() => {
    if (selectedShape && canvasRef?.current && !isDrawing) {
      if (selectedShape.type !== 'group' && selectedShape.type !== 'textbox' && selectedShape.id !== 'customIcon' && selectedShape.id !== 'garbageIcon' && selectedShape.id !== 'customIconNew' && selectedShape.id !== 'draw' && selectedShape?.type !== 'image') {
        canvasRef.current.getActiveObject().set({
          fill: mainColor,
          stroke: strokeColor,
          strokeWidth: strokeWidth
        });
      }
      if (selectedShape.type === 'line') {
        canvasRef.current.getActiveObject().set({
          stroke: fillColor,
          strokeWidth: strokeWidth
        });
      }
      if (selectedShape.id === 'customIcon' || selectedShape.id === 'garbageIcon') {
        canvasRef.current.getActiveObject().set({
          fill: mainColor,
          stroke: selectedShape.id !== 'garbageIcon' ? null : '#ffffff'
        });
      }
      if (selectedShape.id === 'customIconNew') {
        const obj = canvasRef.current.getActiveObject()
        obj.getObjects().forEach((path) => {
          if (path.path) {
            path.set({ fill: mainColor });
          }
        });
      }
      if (selectedShape.id === 'draw') {
        canvasRef.current.getActiveObject().set({
          stroke: strokeColor,
          strokeWidth: strokeWidth
        });
      }
      if (selectedShape.type === 'group'  && selectedShape.id !== 'customIconNew' && selectedShape.shape !== 'arrow' && selectedShape.id !== 'customText') {
        const obj = canvasRef.current.getActiveObject()
        obj.getObjects().forEach((path) => {
          path.set({ fill: mainColor, stroke: strokeColor });
        });
      }
      if (selectedShape.shape === 'arrow' && selectedShape.type === 'group') {
        const obj = canvasRef.current.getActiveObject()
        obj.getObjects().forEach((shape) => {
          if (shape.type === 'path' || shape.shapeType === 'curve') {
            shape.set({ stroke: strokeColor });
          } else {
            if (shape.type === 'triangle') {
              shape.set({ fill: strokeColor });
            }
          }
        });
      }
      if (selectedShape.type === 'textbox') {
        canvasRef.current.getActiveObject().set({
          fill: fillColor,
          fontSize: fontSize,
          fontFamily: fontFamily,
          fontWeight: isBold ? 'bold' : 'normal',
          fontStyle: isItalic ? 'italic' : 'normal'
        });
      }
      if (selectedShape && selectedShape?.shape !== 'arrow') {
        canvasRef.current.getObjects().forEach(obj => {
          if (!obj.controls.deleteControl) {
            obj.controls.deleteControl = new fabric.Control({
              x: 0.5,
              y: -0.5,
              offsetY: -16,
              offsetX: 16,
              cursorStyle: 'pointer',
              mouseUpHandler: deleteObject,
              render: renderIcon(deleteImg),
              cornerSize: 18,
            });
            obj.setCoords()
          }

          if (!obj.controls.cloneControl) {
            obj.controls.cloneControl = new fabric.Control({
              x: -0.5,
              y: -0.5,
              offsetY: -16,
              offsetX: -16,
              cursorStyle: 'pointer',
              mouseUpHandler: cloneObject,
              render: renderIcon(cloneImg),
              cornerSize: 18,
            });
            obj.setCoords()
          }
        });
      } else {
        canvasRef.current.getObjects().forEach(obj => {
          if (obj.controls.cloneControl) {
            delete obj.controls.cloneControl;
          }
          obj.setCoords();
        });
      }
      if (selectedShape && selectedShape?.shape === 'arrow'){
        selectedShape.setControlsVisibility({
          mt: false,
          mb: false,
          ml: false,
          mr: false,
          bl: false,
          br: false,
          tl: false,
          tr: false,
          mtr: true
        })
      }
      canvasRef.current.renderAll();
    }
  }, [selectedShape, canvasRef, fontSize, fillColor, strokeColor, strokeWidth, fontFamily, isBold, isItalic, mainColor]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (canvasRef?.current && selectedShape) {
          if (selectedShape.type !== 'textbox' && !selectedShape.shapeType) {
            canvasRef.current.remove(canvasRef.current.getActiveObject())
            canvasRef.current.renderAll()
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedShape, canvasRef]);

  useEffect(() => {
    const colors = document.querySelectorAll('.color');

    colors.forEach(color => {
      const colorValue = color.getAttribute('data-color');
      color.style.setProperty('--color', colorValue);
    });
  }, []);

  return (
    <div style={{ width: '20%', maxWidth: '198px', background: '#e0e0e0' }} className='d-flex flex-column py-3 px-2 rounded'>
      <div>
        <>
          <div className="d-flex justify-content-between">
            <button className="btn btn-sm btn-transparent text-danger fw-bold p-0"
              disabled={canvasLoading}
              onClick={() => {
                if (canvasRef) {
                  canvasRef.current.getObjects().forEach((obj) => {
                    canvasRef.current?.remove(obj)
                  })
                  canvasRef.current.renderAll()
                }
              }}
            >Clear All</button>
            {selectedShape && <button className="btn btn-sm btn-transparent text-danger fw-bold p-0"
              onClick={() => {
                if (canvasRef) {
                  canvasRef.current.remove(canvasRef.current.getActiveObject())
                  canvasRef.current.renderAll()
                }
              }}
            >Remove Shape</button>}
            {isDrawing && shapeType === 'draw' && <button className="btn btn-sm btn-transparent text-danger fw-bold p-0"
              onClick={() => {
                if (canvasRef) {
                  canvasRef.current.isDrawingMode = false
                  setIsDrawing(false)
                  canvasRef.current.defaultCursor = 'default';
                  setPolygonPoints([]);
                  setLines([]);
                  setShapeType(null)
                }
              }}
            >Stop Drawing</button>}
          </div>
          <div className='separator my-1' style={{ border: '1px solid #ccc' }}></div>
        </>
      </div>
      <h4 className='text-center fw-bold mb-2'>Shapes</h4>
      <div className="d-flex flex-col flex-wrap gap-2 shape-container">
        {shapes.map((shape) => {
          const ShapeIcon = {
            Square: Square,
            Circle: Circle,
            Triangle: Triangle,
            Rectangle: RectangleHorizontal,
            Stop: Octagon,
            Quote: MessageSquare,
            Arrow: MoveRight,
            Line: Minus,
            Pin: MapPin,
            Type: Type,
            Draw: Pen,
          }[shape];
          return (
            <button
              key={shape}
              disabled={canvasLoading}
              onClick={() => {
                if (canvasRef) {
                  canvasRef.current.isDrawingMode = false
                  setIsDrawing(false)
                  canvasRef.current.defaultCursor = 'default';
                  setPolygonPoints([]);
                  setLines([]);
                  setShapeType(null)
                  onShapeSelection(shape)
                }
              }
              }
              className={`p-2 rounded shape-btn text-black bg-hover-light m-0 ${(isDrawing && (shapeType?.toLowerCase() === shape?.toLowerCase())) && 'bg-light'}`}
            >
              {
                (['ArrowCurve', 'PolygonShape', 'Logo', 'LandingGear', 'Enter', 'Exit', 'Garbage', 'TruckSide', 'TruckTop', 'TrailerSide', 'TrailerTop', 'Bathroom1', 'Bathroom2', 'Bathroom3', 'Bathroom4', 'Shower', 'StopSign', 'GateAccess'].includes(shape)) ?
                  <img src={toAbsoluteUrl(`/media/logos/${shape.toLowerCase()}.png`)} alt={`${shape.toLowerCase()}-icon`} width={24} height={24} style={{ transform: 'scale(1.2)', objectFit: 'contain' }} />
                  :
                  <ShapeIcon className="w-6 h-6" />
              }
            </button>
          );
        })}
      </div>
      <div className='separator my-3' style={{ border: '1px solid #ccc' }}></div>
      <h4 className='text-center fw-bold mb-2'>Settings</h4>
      <div className="color-selection mb-3">
        <button className={`fw-bold ${colorType === 'fill' ? 'active' : ''}`} onClick={() => setColorType('fill')}>Fill Color</button>
        <button className={`fw-bold ${colorType === 'stroke' ? 'active' : ''}`} onClick={() => setColorType('stroke')}>Stroke Color</button>
      </div>
      <div className="d-flex flex-wrap gap-2 w-100 color-picker">
        {
          (colorType === 'fill' ? colors : strokes).map((color, index) => (
            <div key={index} className={`color ${colorType === 'fill' ? (fillColor === color ? 'active' : '') : (strokeColor === color ? 'active' : '')}`} data-color={color}
              onClick={() => {
                if (colorType === 'fill') {
                  setFillColor(color)
                  setMainColor(hexToRgba(color, 1))
                } else {
                  setStrokeColor(color)
                }
              }}
            ></div>
          ))
        }
      </div>
      {!['textbox', 'line']?.includes(selectedShape?.type) ?
        <div className="mt-5">
          <AlphaPicker width="95%" color={mainColor} onChange={(color) => {
            setFillColor(color.hex)
            setMainColor(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)
          }}
          />
        </div>
        :
        selectedShape?.type === 'textbox' &&
        <div className="d-flex justify-content-between mt-3 gap-2">
          <select
            className="form-select form-select-solid fw-bolder"
            style={{ padding: '5px', backgroundPosition: 'right 5px center' }}
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
          >
            {fontFamilies.map((font) => (
              <option key={font} value={font}>{font}</option>
            ))}
          </select>
          <select
            className="form-select form-select-solid fw-bolder"
            style={{ padding: '5px 20px 5px 5px', width: 'max-content', backgroundPosition: 'right 5px center' }}
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value)}
          >
            {fontSizes.map((size) => (
              <option key={size} value={size}>{size}px</option>
            ))}
          </select>
        </div>
      }
      <div className="d-flex justify-content-between align-items-center gap-3 mt-2 position-relative">
        <button
          onClick={onBringToFront}
          className={`p-2 rounded shape-btn text-black bg-hover-light w-100`}
        >
          <BringToFront />
        </button>
        {['textbox']?.includes(selectedShape?.type) ?
          <>
            <button
              onClick={() => setIsBold(!isBold)}
              className={`p-2 rounded shape-btn text-black bg-hover-light w-100 ${isBold && 'active'}`}
            >
              <Bold />
            </button>
            <button
              onClick={() => setIsItalic(!isItalic)}
              className={`p-2 rounded shape-btn text-black bg-hover-light w-100 ${isItalic && 'active'}`}
            >
              <Italic />
            </button>
          </>
          :
          <select
            className="form-select form-select-solid fw-bolder"
            style={{ padding: '7px 5px', backgroundPosition: 'right 5px center' }}
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(+e.target.value)}
          >
            {strokeSizes.map((size) => (
              <option key={size} value={size}>{size}px</option>
            ))}
          </select>
        }
      </div>
      <div className='separator my-2' style={{ border: '1px solid #ccc' }}></div>
      <div className="d-flex justify-content-between gap-3">
        <button
          disabled={canvasLoading}
          onClick={() => onFlip('horizontal')}
          className="p-2 rounded shape-btn text-black bg-hover-light w-100"
        >
          <FlipHorizontal />
        </button>
        <button
          disabled={canvasLoading}
          onClick={() => onFlip('vertical')}
          className="p-2 rounded shape-btn text-black bg-hover-light w-100"
        >
          <FlipVertical />
        </button>
        <button
          disabled={canvasLoading}
          onClick={() => onRotate('left')}
          className="p-2 rounded shape-btn text-black bg-hover-light w-100"
        >
          <RotateCcw />
        </button>
        <button
          disabled={canvasLoading}
          onClick={() => onRotate('right')}
          className="p-2 rounded shape-btn text-black bg-hover-light w-100"
        >
          <RotateCw />
        </button>
      </div>
      <div className='separator my-3' style={{ border: '1px solid #ccc' }}></div>
      {imagePreview && <button className="btn btn-sm btn-main text-white fw-bold mt-auto"
        disabled={canvasLoading || loading}
        onClick={() => onSaveImage()}
      >Save</button>
      }
    </div >
  );
};

export default Sidebar;